import React, { useState, useCallback, ReactNode } from 'react';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { GradientVariant } from '@/components/ui/GradientButton';

interface ConfirmationOptions {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: GradientVariant;
  cancelVariant?: GradientVariant;
}

interface UseConfirmationDialogReturn {
  showConfirmation: (options: ConfirmationOptions) => Promise<boolean>;
  ConfirmationDialog: () => ReactNode;
}

/**
 * Hook that provides a beautiful confirmation dialog instead of browser confirm()
 * Returns a promise that resolves to true if confirmed, false if cancelled
 */
export function useConfirmationDialog(): UseConfirmationDialogReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmationOptions | null>(null);
  const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null);

  const showConfirmation = useCallback((confirmOptions: ConfirmationOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setOptions(confirmOptions);
      setResolvePromise(() => resolve);
      setIsOpen(true);
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    if (resolvePromise) {
      resolvePromise(true);
      setResolvePromise(null);
    }
  }, [resolvePromise]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    if (resolvePromise) {
      resolvePromise(false);
      setResolvePromise(null);
    }
  }, [resolvePromise]);

  const ConfirmationDialog = useCallback(() => {
    if (!options) return null;

    return (
      <ConfirmDialog
        isOpen={isOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title={options.title}
        description={options.description}
        confirmText={options.confirmText || 'Confirm'}
        cancelText={options.cancelText || 'Cancel'}
        confirmVariant={options.confirmVariant || 'red'}
        cancelVariant={options.cancelVariant || 'green'}
      />
    );
  }, [isOpen, options, handleConfirm, handleCancel]);

  return {
    showConfirmation,
    ConfirmationDialog
  };
} 